// components/modals/MassTaxUpdateModal.jsx
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
// import Card from '../ui/Card';
import Card from '../components/ui/Card';
// import Button from '../ui/Button';
import Button from '../components/ui/Button';
// import Input from '../ui/Input';
import Input from '../components/ui/Input';
// import { useToast } from '../../hooks/useToast';
import { useToast } from '../hooks/useToast';
// import { readTaxes, fetchBatchTaxToCategoryItemUtil } from '../../apiHelper';
import { readTaxes, fetchBatchTaxToCategoryItemUtil, client } from '../apiHelper';

const MassTaxUpdateModal = ({ onClose, user }) => {
    const { showToast } = useToast();

    const [categories, setCategories] = useState([]);
    const [taxRates, setTaxRates] = useState([]);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [selectedAction, setSelectedAction] = useState(''); // 'apply' | 'remove' | ''
    const [selectedTaxId, setSelectedTaxId] = useState('');
    const [confirmationText, setConfirmationText] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Load data
    useEffect(() => {
        const load = async () => {
            try {
                const [catData, taxData] = await Promise.all([
                    fetchBatchTaxToCategoryItemUtil(user),
                    readTaxes(user),
                ]);
                console.log(catData)
                setCategories(catData || []);
                setTaxRates(taxData || []);
            } catch (err) {
                showToast('Failed to load categories or tax rates', 'error');
            } finally {
                setLoading(false);
            }
        };
        if (user) load();
    }, [user, showToast]);

    const totalProducts = selectedCategoryIds.reduce((sum, id) => {
        const cat = categories.find(c => c.category_id === id);
        return sum + (cat?.catentry_ids?.length || 0);
    }, 0);

    const affectedCatentryIds = selectedCategoryIds.flatMap(id =>
        categories.find(c => c.category_id === id)?.catentry_ids || []
    );

    const canProceed = selectedCategoryIds.length > 0 && selectedAction !== '';
    const isConfirmed = confirmationText.trim().toUpperCase() === 'APPLY TAX';
    const canSubmit = canProceed && isConfirmed && !submitting;

    const selectedTax = taxRates.find(t => t.taxcategory_id === Number(selectedTaxId));

    const toggleCategory = (id) => {
        setSelectedCategoryIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);

        const payload = {
            action: selectedAction,
            catentry_ids: affectedCatentryIds,
        };

        if (selectedAction === 'apply' && selectedTaxId) {
            payload.taxcategory_id = Number(selectedTaxId);
        }

        try {
            // console.log('BATCH TAX UPDATE →', payload);
            const response = await client.postData('/product/batch-add-tax-to-catentries', payload);
            showToast(response?.msg || `Tax rules applied to ${totalProducts} products!`, 'success');
            onClose();
        } catch (err) {
            showToast(err.response.data.detail || 'Failed to apply tax rules', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
                <Card className="p-12 text-center">
                    <Loader2 className="h-12 w-12 animate-spin text-gmail-blue mx-auto mb-4" />
                    <p className="text-text-secondary">Loading categories and tax rates...</p>
                </Card>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 overflow-y-auto p-4">
            <Card className="w-full max-w-4xl max-h-screen overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 p-6">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
                        <AlertTriangle className="text-orange-500" size={28} />
                        Mass Tax Rule Update
                    </h2>
                    <Button variant="ghost" size="sm" onClick={onClose} disabled={submitting}>
                        <X size={22} />
                    </Button>
                </div>

                <div className="p-6 space-y-8">
                    {/* Step 1 */}
                    <div>
                        <h3 className="text-lg font-semibold mb-2">1. Select Categories</h3>
                        <p className="text-sm text-text-secondary mb-4">
                            Choose categories to update tax rules for all products inside.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto border rounded-lg p-4 bg-gray-50">
                            {categories.map(cat => (
                                <label
                                    key={cat.category_id}
                                    className="flex items-center gap-4 p-4 border rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition"
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedCategoryIds.includes(cat.category_id)}
                                        onChange={() => toggleCategory(cat.category_id)}
                                        className="w-5 h-5 text-gmail-blue rounded focus:ring-gmail-blue"
                                    />
                                    <div>
                                        <div className="font-medium text-text-primary">{cat.identifier}</div>
                                        <div className="text-sm text-text-secondary">
                                            {cat.catentry_ids?.length || 0} product{cat.catentry_ids?.length !== 1 ? 's' : ''}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>

                        {selectedCategoryIds.length > 0 && (
                            <div className="mt-4 p-4 bg-blue-50 border border-blue-300 rounded-lg">
                                <p className="font-bold text-gmail-blue">
                                    {selectedCategoryIds.length} category{selectedCategoryIds.length > 1 ? 'ies' : ''} selected →{' '}
                                    <strong>{totalProducts}</strong> product{totalProducts !== 1 ? 's' : ''} affected
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Step 2 */}
                    <div>
                        <h3 className="text-lg font-semibold mb-4">2. Choose Action</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Action</label>
                                <select
                                    value={selectedAction}
                                    onChange={(e) => {
                                        setSelectedAction(e.target.value);
                                        if (e.target.value === 'remove') setSelectedTaxId('');
                                    }}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gmail-blue focus:border-gmail-blue"
                                >
                                    <option value="">-- Select action --</option>
                                    <option value="apply">Apply a Tax Rate</option>
                                    <option value="remove">Remove All Tax (Set to Exempt)</option>
                                </select>
                            </div>

                            {selectedAction === 'apply' && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Tax Rate</label>
                                    <select
                                        value={selectedTaxId}
                                        onChange={(e) => setSelectedTaxId(e.target.value)}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gmail-blue focus:border-gmail-blue"
                                    >
                                        <option value="">-- Select tax rate --</option>
                                        {taxRates.map(tax => (
                                            <option key={tax.taxcategory_id} value={tax.taxcategory_id}>
                                                {tax.code} – {tax.identifier} ({(tax.rate).toFixed(1)}%)
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Step 3 */}
                    {canProceed && (
                        <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6">
                            <h3 className="text-xl font-bold text-red-800 mb-4 flex items-center gap-2">
                                <AlertTriangle size={28} />
                                Final Confirmation Required
                            </h3>
                            <p className="text-red-700 mb-3">
                                You are about to <strong>{selectedAction === 'apply' ? `APPLY "${selectedTax?.code || ''}"` : 'REMOVE ALL TAX'}</strong>
                            </p>
                            <p className="text-red-700 font-bold text-lg mb-4">
                                {totalProducts} product{totalProducts !== 1 ? 's' : ''} across {selectedCategoryIds.length} category{selectedCategoryIds.length > 1 ? 'ies' : ''}
                            </p>
                            <p className="text-red-700 mb-6">
                                This action is <strong>immediate and irreversible</strong>.
                            </p>

                            <label className="block font-bold text-red-800 mb-2">
                                Type <span className="font-mono bg-red-200 px-3 py-1 rounded">APPLY TAX</span> to confirm:
                            </label>
                            <Input
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                placeholder="APPLY TAX"
                                className="text-lg font-mono tracking-wider border-red-500 focus:border-red-600"
                            />
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-4 p-6 border-t bg-gray-50">
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 min-w-64"
                    >
                        {submitting ? (
                            <>
                                <Loader2 className="animate-spin" size={20} />
                                Applying...
                            </>
                        ) : (
                            <>
                                <CheckCircle size={20} />
                                Apply to {totalProducts} Product{totalProducts !== 1 ? 's' : ''}
                            </>
                        )}
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default MassTaxUpdateModal;